import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import type {
  NotificationDomainsView,
  PutNotificationDomainsBody,
} from "@shared/types/notificationDomains";
import { SCOPED_RULES_PATH } from "@/hooks/use-alert-routing";
import { NOTIFICATION_RULES_PATH } from "@/hooks/use-notifications";
import { notificationDomainsPath } from "@/hooks/notificationDomainsKey";
import { apiRequest } from "@/lib/api";

/**
 * #1111: the tenant's Allowed notification domains, `GET|PUT /api/v2/tenants/:id/notification-domains`
 * (`tenant.instances.manage`, TenantAdmin and above). A save refreshes the Notifications tab's
 * rules views, whose `recipientPolicy` marks and refuses addresses by it.
 */

export { notificationDomainsPath };

export function useNotificationDomains(
  tenantId: string | null,
  enabled = true,
): UseQueryResult<NotificationDomainsView> {
  return useQuery<NotificationDomainsView>({
    queryKey: [tenantId ? notificationDomainsPath(tenantId) : "notification-domains-disabled"],
    enabled: enabled && Boolean(tenantId),
  });
}

export function useSaveNotificationDomains(tenantId: string | null) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: PutNotificationDomainsBody) =>
      apiRequest<NotificationDomainsView>(notificationDomainsPath(tenantId ?? ""), {
        method: "PUT",
        body,
        // It decides where every instance's alerts may go: a recent sign-in, like the rules.
        stepUp: "change",
      }),
    onSuccess: (view) => {
      client.setQueryData([notificationDomainsPath(view.tenantId)], view);
      void client.invalidateQueries({ queryKey: [NOTIFICATION_RULES_PATH] });
      void client.invalidateQueries({ queryKey: [SCOPED_RULES_PATH] });
    },
  });
}
